import axios from "axios"
import { useMemo } from "react"
import useSWR from "swr"
import { getCurrentBranch, getUser } from "helpers/auth-helper"

const fetcher = async (url, params) => {
    return await axios.get(url, { params }).then(res => res.data.data)
}

const isSuperUser = () => {
    const user = getUser()
    const currentBranch = getCurrentBranch()

    if (!user) return false
    if (currentBranch?.roles?.find((role) => role.is_super === 1)) return true
    if (user?.roles?.find((role) => role.is_super === 1)) return true

    return false
}

// Outlet konsinyasi yoiboi tidak boleh muncul di filter-by-outlet (hanya difilter, bukan dihapus dari form)
const YOIBOI_OUTLET_CODE = "yoiboi"
const isYoiboiOutlet = (row) => String(row?.code ?? "").toLowerCase() === YOIBOI_OUTLET_CODE

const excludeYoiboiOutlet = (data) => {
    if (data == null) return data

    if (Array.isArray(data)) {
        return data.filter((row) => !isYoiboiOutlet(row))
    }

    if (Array.isArray(data?.data)) {
        return {
            ...data,
            data: data.data.filter((row) => !isYoiboiOutlet(row)),
        }
    }

    return data
}

// ATALLA-403: pemegang node ini boleh memilih SEMUA outlet dari ENTITAS-nya
// (mis. admin HO PT Atalla melihat semua outlet ATALLA) di filter outlet semua menu.
const FILTER_ENTITY_NODE = "outlet.filter-entity"

const hasFilterEntityNode = () => {
    const currentBranch = getCurrentBranch()
    const roles = currentBranch?.roles ?? getUser()?.roles ?? []
    for (const role of roles) {
        if ((role?.permissions ?? []).find((p) => p?.node === FILTER_ENTITY_NODE)) return true
    }
    return false
}

const restrictBranches = (data) => {
    if (data == null) return data
    if (isSuperUser()) return data

    const currentBranch = getCurrentBranch()
    if (!currentBranch?.id) return data

    // ATALLA-403: pemegang permission → tampilkan semua outlet se-ENTITAS branch aktifnya.
    if (hasFilterEntityNode() && currentBranch?.entity) {
        const entity = String(currentBranch.entity)
        const byEntity = (row) => String(row?.entity ?? "") === entity

        if (Array.isArray(data)) return data.filter(byEntity)
        if (Array.isArray(data?.data)) return { ...data, data: data.data.filter(byEntity) }
        return data
    }

    const allowedId = String(currentBranch.id)

    if (Array.isArray(data)) {
        return data.filter((row) => String(row?.id ?? "") === allowedId)
    }

    if (Array.isArray(data?.data)) {
        return {
            ...data,
            data: data.data.filter((row) => String(row?.id ?? "") === allowedId),
        }
    }

    return data
}

// 290726 (penegasan user): SEMUA pilihan/filter outlet urut ASCENDING by id.
// Diterapkan di sini supaya ~39 halaman yang mengambil outlet lewat hook ini ikut
// tanpa sort sendiri-sendiri (pasangan dari sortBranchesByIdAsc di general-helper,
// yang mengurus jalur getBranchList/localStorage). Bentuk data bisa array polos
// ATAU objek terpaginasi {data: []} — keduanya ditangani, dan selalu pada SALINAN.
const sortBranchesByIdAsc = (data) => {
    if (data == null) return data

    const byIdAsc = (a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0)

    if (Array.isArray(data)) return [...data].sort(byIdAsc)
    if (Array.isArray(data?.data)) return { ...data, data: [...data.data].sort(byIdAsc) }

    return data
}

const useBranchRepositories = (params) => {
    // excludeYoiboi & unrestricted tidak ikut dikirim ke API (hanya flag sisi client),
    // supaya cache SWR tetap sama.
    // ATALLA-389: unrestricted=true melewati restrictBranches (non-super → outlet sendiri)
    // untuk halaman lintas-outlet yang di-gate permission (mis. product-stock.cross-outlet).
    const { excludeYoiboi, unrestricted, ...fetchParams } = params || {}
    const { data, error, mutate } = useSWR([`${process.env.REACT_APP_GENERIC_API_URL}/branch`, fetchParams], fetcher)

    const restricted = useMemo(() => {
        const base = unrestricted ? data : restrictBranches(data)
        return sortBranchesByIdAsc(excludeYoiboi ? excludeYoiboiOutlet(base) : base)
    }, [data, excludeYoiboi, unrestricted])

    return {
        data: restricted,
        isLoading: !error && !data,
        error,
        mutate
    }
}

export default useBranchRepositories